/**
 * Composant de Gestion Financiere DOS-DOMIUM pour Super Admin
 * Orchestrateur principal - Refactored
 */
import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  RefreshCw, CheckCircle, Clock, Euro, Search,
  Home, Filter, Loader2,
  PieChart, LayoutDashboard, Target, Wallet, BookOpen, Landmark
} from 'lucide-react';

import { formatMoney } from './constants';
import PaiementsTab from './PaiementsTab';
import AcomptesTab from './AcomptesTab';
import ComptabiliteTab from './ComptabiliteTab';
import SouscriptionsTab from './SouscriptionsTab';
import EcheanciersTab from './EcheanciersTab';
import ArchivesTab from './ArchivesTab';
import JournalComptableTab from './JournalComptableTab';
import SuiviSEPATab from './SuiviSEPATab';
import Modals from './Modals';
import { printRecuEcheance, printRecuAcompte, printRecuContributionAcquitte, printEcheancier } from './utils/printReceipts';

// Custom hooks
import useFinanceData from './hooks/useFinanceData';
import usePayments from './hooks/usePayments';
import useComptabilite from './hooks/useComptabilite';
import useEcheancier from './hooks/useEcheancier';

export default function GestionFinanciereDOMIUM() {
  const [activeTab, setActiveTab] = React.useState('paiements');

  const data = useFinanceData();
  const payments = usePayments({
    getAuthHeader: data.getAuthHeader, getAmount: data.getAmount, getMode: data.getMode,
    getName: data.getName, getContributionStatus: data.getContributionStatus, isPaid: data.isPaid,
    generateRecuReference: data.generateRecuReference, getAllPayments: data.getAllPayments,
    fetchData: data.fetchData,
  });
  const compta = useComptabilite({ getAuthHeader: data.getAuthHeader });
  const ech = useEcheancier({
    getAuthHeader: data.getAuthHeader, getAmount: data.getAmount,
    isPaid: data.isPaid, fetchData: data.fetchData,
  });

  // Load compta stats when tab changes
  useEffect(() => {
    if (activeTab === 'comptabilite') compta.fetchComptaStats();
  }, [activeTab]);

  // Print wrappers (pass helpers to utility functions)
  const helpers = {
    getName: data.getName, getAmount: data.getAmount, isPaid: data.isPaid,
    getContributionStatus: data.getContributionStatus,
    generateAcompteRecuReference: data.generateAcompteRecuReference,
    getAllPayments: data.getAllPayments,
  };
  const handlePrintRecuEcheance = (doc, echeanceIndex, recuRef) =>
    printRecuEcheance(doc, echeanceIndex, recuRef, helpers);
  const handlePrintRecuAcompte = (doc, acompte, acompteIndex) =>
    printRecuAcompte(doc, acompte, acompteIndex, helpers);
  const handlePrintRecuContributionAcquitte = (doc) =>
    printRecuContributionAcquitte(doc, helpers);
  const handlePrintEcheancier = (doc) =>
    printEcheancier(doc, helpers);

  const _handleDownloadRecuEcheance = (doc, echeanceIndex) => {
    const recuRef = data.generateRecuReference(doc, echeanceIndex);
    handlePrintRecuEcheance(doc, echeanceIndex, recuRef);
  };

  if (data.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Home className="h-6 w-6" />
              <div>
                <CardTitle>Gestion Financiere DOS-DOMIUM</CardTitle>
                <CardDescription className="text-white/80">Factures, recus ESS et validation des paiements</CardDescription>
              </div>
            </div>
            <Button variant="secondary" onClick={() => data.fetchData(true)} disabled={data.refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${data.refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-slate-800 text-white"><CardContent className="pt-4"><LayoutDashboard className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{data.stats.total}</p><p className="text-xs opacity-70">Total</p></CardContent></Card>
        <Card className="bg-amber-500 text-white"><CardContent className="pt-4"><Clock className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{data.stats.pending}</p><p className="text-xs opacity-70">En attente</p></CardContent></Card>
        <Card className="bg-emerald-600 text-white"><CardContent className="pt-4"><CheckCircle className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{data.stats.paid}</p><p className="text-xs opacity-70">Valides</p></CardContent></Card>
        <Card className="bg-blue-600 text-white"><CardContent className="pt-4"><Euro className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{formatMoney(data.stats.paidAmount)}</p><p className="text-xs opacity-70">Encaisse</p></CardContent></Card>
        <Card className="bg-purple-600 text-white"><CardContent className="pt-4"><Target className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{data.stats.rate}%</p><p className="text-xs opacity-70">Recouvrement</p></CardContent></Card>
        <Card className="bg-indigo-600 text-white"><CardContent className="pt-4"><PieChart className="h-5 w-5 mb-1 opacity-70" /><p className="text-2xl font-bold">{formatMoney(data.stats.totalAmount)}</p><p className="text-xs opacity-70">Total</p></CardContent></Card>
      </div>

      {/* Progress */}
      <Card><CardContent className="py-4"><div className="flex justify-between mb-2"><span>Progression du recouvrement</span><span className="font-bold text-indigo-600">{data.stats.rate}%</span></div><Progress value={parseFloat(data.stats.rate)} className="h-3" /></CardContent></Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="paiements">Paiements</TabsTrigger>
          <TabsTrigger value="acomptes" className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />Acomptes ({data.acomptesStats.total})</TabsTrigger>
          <TabsTrigger value="comptabilite" className="flex items-center gap-1 bg-gradient-to-r from-emerald-50 to-teal-50"><PieChart className="h-3.5 w-3.5 text-emerald-600" />Comptabilite &amp; Exports</TabsTrigger>
          <TabsTrigger value="souscriptions">Souscriptions</TabsTrigger>
          <TabsTrigger value="echeanciers">Echeanciers</TabsTrigger>
          <TabsTrigger value="suivi-sepa" className="flex items-center gap-1 bg-gradient-to-r from-blue-50 to-indigo-50">
            <Landmark className="h-3.5 w-3.5 text-blue-700" />Suivi SEPA
          </TabsTrigger>
          <TabsTrigger value="journal-comptable" className="flex items-center gap-1 bg-gradient-to-r from-amber-50 to-orange-50">
            <BookOpen className="h-3.5 w-3.5 text-amber-700" />Journal Comptable
          </TabsTrigger>
          <TabsTrigger value="archives">Archives</TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mt-4">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500 mb-1 block">Recherche</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input placeholder="Reference, nom, SIRET..." value={data.searchTerm} onChange={(e) => data.setSearchTerm(e.target.value)} className="pl-10" />
                </div>
              </div>
              <div className="w-44">
                <Label className="text-xs text-gray-500 mb-1 block">Statut paiement</Label>
                <Select value={data.filterStatus} onValueChange={data.setFilterStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="partial">Partiellement paye</SelectItem>
                    <SelectItem value="paid">Paye</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-52">
                <Label className="text-xs text-gray-500 mb-1 block">Statut recu contribution</Label>
                <Select value={data.filterRecuStatus} onValueChange={data.setFilterRecuStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les recus</SelectItem>
                    <SelectItem value="recu_pending">Recu en attente</SelectItem>
                    <SelectItem value="recu_partial">Recu partiel</SelectItem>
                    <SelectItem value="recu_paid">Recu paye</SelectItem>
                    <SelectItem value="recu_acquitted">Recu ACQUITTE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={() => { data.setFilterStatus('all'); data.setFilterRecuStatus('all'); data.setSearchTerm(''); }}>
                <RefreshCw className="h-4 w-4 mr-1" /> Reinitialiser
              </Button>
            </div>
            {(data.filterStatus !== 'all' || data.filterRecuStatus !== 'all' || data.searchTerm) && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <Filter className="h-4 w-4" /><span>Filtres actifs:</span>
                {data.searchTerm && <Badge variant="outline">Recherche: &quot;{data.searchTerm}&quot;</Badge>}
                {data.filterStatus !== 'all' && <Badge variant="outline">Paiement: {data.filterStatus}</Badge>}
                {data.filterRecuStatus !== 'all' && <Badge variant="outline">Recu: {data.filterRecuStatus.replace('recu_', '')}</Badge>}
                <span className="ml-auto font-medium">{data.filtered.length} resultat(s)</span>
              </div>
            )}
          </CardContent>
        </Card>

        <TabsContent value="paiements" className="space-y-4">
          <PaiementsTab archiveDoc={payments.archiveDoc} downloadDoc={payments.downloadDoc} expandedIds={data.expandedIds} filtered={data.filtered}
            generateRecuReference={data.generateRecuReference} getAllPayments={data.getAllPayments} getAmount={data.getAmount}
            getContributionStatus={data.getContributionStatus} getMode={data.getMode} getName={data.getName}
            handleMarkAcquitted={payments.handleMarkAcquitted} handlePrintEcheancier={handlePrintEcheancier}
            handlePrintRecuAcompte={handlePrintRecuAcompte} handlePrintRecuContributionAcquitte={handlePrintRecuContributionAcquitte}
            handlePrintRecuEcheance={handlePrintRecuEcheance} isPaid={data.isPaid}
            openEcheanceModal={ech.openEcheanceModal} openPaymentModal={payments.openPaymentModal}
            openEditPaymentDateModal={payments.openEditPaymentDateModal}
            openSEPAModal={payments.openSEPAModal} openScheduleModal={ech.openScheduleModal}
            openStripeModal={payments.openStripeModal} processing={payments.processing}
            setPaymentForm={payments.setPaymentForm} toggleExpand={data.toggleExpand} />
        </TabsContent>

        <TabsContent value="acomptes" className="space-y-4">
          <AcomptesTab acomptesStats={data.acomptesStats} expandedIds={data.expandedIds} filterAcompteStatus={data.filterAcompteStatus}
            generateAcompteRecuReference={data.generateAcompteRecuReference} getAllAcomptesFlat={data.getAllAcomptesFlat}
            handlePrintRecuAcompte={handlePrintRecuAcompte} openDeleteAcompteModal={payments.openDeleteAcompteModal}
            openEditAcompteModal={payments.openEditAcompteModal} setFilterAcompteStatus={data.setFilterAcompteStatus} toggleExpand={data.toggleExpand} />
        </TabsContent>

        <TabsContent value="comptabilite" className="space-y-6">
          <ComptabiliteTab comptaDateDebut={compta.comptaDateDebut} comptaDateFin={compta.comptaDateFin}
            comptaPaymentRef={compta.comptaPaymentRef} comptaStats={compta.comptaStats} exportLoading={compta.exportLoading}
            fetchComptaStats={compta.fetchComptaStats} filtered={data.filtered} getAmount={data.getAmount}
            getAuthHeader={data.getAuthHeader} handleExport={compta.handleExport} handleExportExcelComplet={compta.handleExportExcelComplet}
            isPaid={data.isPaid} loadingStats={compta.loadingStats} setComptaDateDebut={compta.setComptaDateDebut}
            setComptaDateFin={compta.setComptaDateFin} setComptaPaymentRef={compta.setComptaPaymentRef}
            setPeriode={compta.setPeriode} stats={data.stats} />
        </TabsContent>

        <TabsContent value="souscriptions" className="space-y-4">
          <SouscriptionsTab expandedIds={data.expandedIds} filtered={data.filtered} getAmount={data.getAmount}
            getMode={data.getMode} getName={data.getName} handlePrintRecuContributionAcquitte={handlePrintRecuContributionAcquitte}
            isPaid={data.isPaid} setActiveTab={setActiveTab} setSelectedDoc={payments.setSelectedDoc} toggleExpand={data.toggleExpand} />
        </TabsContent>

        <TabsContent value="echeanciers" className="space-y-4">
          <EcheanciersTab expandedIds={data.expandedIds} filtered={data.filtered} getName={data.getName}
            handleDeleteEcheancier={ech.handleDeleteEcheancier} handlePrintEcheancier={handlePrintEcheancier}
            handleResetEcheancier={ech.handleResetEcheancier} toggleExpand={data.toggleExpand} />
        </TabsContent>

        <TabsContent value="journal-comptable" className="space-y-4">
          <JournalComptableTab getAuthHeader={data.getAuthHeader} />
        </TabsContent>

        <TabsContent value="suivi-sepa" className="space-y-4">
          <SuiviSEPATab getAuthHeader={data.getAuthHeader} />
        </TabsContent>

        <TabsContent value="archives" className="space-y-4">
          <ArchivesTab documents={data.documents} expandedIds={data.expandedIds} filtered={data.filtered}
            getAmount={data.getAmount} getName={data.getName} isPaid={data.isPaid} setActiveTab={setActiveTab} toggleExpand={data.toggleExpand} />
        </TabsContent>
      </Tabs>

      {/* All Modals */}
      <Modals
        echeanceDate={ech.echeanceDate} editAcompteForm={payments.editAcompteForm} getAmount={data.getAmount}
        getAuthHeader={data.getAuthHeader} getName={data.getName} handleDeleteAcompte={payments.handleDeleteAcompte}
        handleEcheance={ech.handleEcheance} handleEditAcompte={payments.handleEditAcompte} handlePayment={payments.handlePayment}
        handleSchedule={ech.handleSchedule} handleSetupSEPA={payments.handleSetupSEPA} handleStripePayment={payments.handleStripePayment}
        handleUpdatePaymentDate={payments.handleUpdatePaymentDate}
        editPaymentDateForm={payments.editPaymentDateForm} setEditPaymentDateForm={payments.setEditPaymentDateForm}
        showEditPaymentDateModal={payments.showEditPaymentDateModal} setShowEditPaymentDateModal={payments.setShowEditPaymentDateModal}
        paymentForm={payments.paymentForm} processing={payments.processing} scheduleItems={ech.scheduleItems}
        selectedAcompte={payments.selectedAcompte} selectedAcompteIndex={payments.selectedAcompteIndex}
        selectedDoc={payments.selectedDoc} selectedEcheanceIndex={payments.selectedEcheanceIndex}
        sepaForm={payments.sepaForm} setEcheanceDate={ech.setEcheanceDate} setEditAcompteForm={payments.setEditAcompteForm}
        setPaymentForm={payments.setPaymentForm} setProcessing={payments.setProcessing}
        setScheduleItems={ech.setScheduleItems} setSepaForm={payments.setSepaForm}
        setShowDeleteAcompteModal={payments.setShowDeleteAcompteModal} setShowEcheanceModal={ech.setShowEcheanceModal}
        setShowEditAcompteModal={payments.setShowEditAcompteModal} setShowPaymentModal={payments.setShowPaymentModal}
        setShowSEPAModal={payments.setShowSEPAModal} setShowScheduleModal={ech.setShowScheduleModal}
        setShowStripeModal={payments.setShowStripeModal} setStripePaymentType={payments.setStripePaymentType}
        showDeleteAcompteModal={payments.showDeleteAcompteModal} showEcheanceModal={ech.showEcheanceModal}
        showEditAcompteModal={payments.showEditAcompteModal} showPaymentModal={payments.showPaymentModal}
        showSEPAModal={payments.showSEPAModal} showScheduleModal={ech.showScheduleModal}
        showStripeModal={payments.showStripeModal} stripePaymentType={payments.stripePaymentType} />
    </div>
  );
}
